import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { getQueuesAPI } from '../util.js';

const ConcessionScreenController = ({ children }) => {
  const { token } = useParams();
  useEffect(() => {
    if (!token || token === 'index.html') {
      // Invalid token - maybe redirect or show error
      return;
    }
    // fetch with token
  }, [token]);
  // State management
  const [loading, setLoading] = useState(true);
  const [currentSection, setCurrentSection] = useState(null);
  const [league, setLeague] = useState(null);
  const [venue, setVenue] = useState(null);
  const [concessionLocationStaticData, setConcessionLocationStaticData] = useState(null);
  const [concessionLocationSections, setConcessionLocationSections] = useState([]);
  const [nearbyCurrentSection, setNearbyCurrentSection] = useState(null);
  const [currentWaitTimeData, setCurrentWaitTimeData] = useState([]);
  const [theme, setTheme] = useState(null);

  // Get theme based on league and venue
  const getTheme = (league, venue) => {
    try {
      return require(`../clients/${league}/${venue}/${venue}_themes.js`).default;
    } catch (error) {
      console.warn(`Theme not found for ${league}/${venue}, using default`);
      return require(`../clients/mlb/braves/braves_themes.js`).default;
    }
  };

  // Fetch current wait time data
  // Do NOT set loading for this data call, we want an uninterrupted screen
  const fetchCurrentWaitTimeData = (sections) => {
    if (!token || !sections || sections.length === 0) {
      console.log('Missing token or sections for wait time fetch');
      return;
    }

    console.log('Fetching current wait time data for sections:', sections);

    getQueuesAPI(
      "get-concession-location-current-data-screen",
      { sections }, // Pass sections array in request body
      (data, error) => {
        if (error) {
          console.error('Failed to fetch current wait time data:', error);
        } else if (data) {
          console.log('Current wait time data loaded:', data);
          setCurrentWaitTimeData(data);

          // DEV ONLY: Override with random queue times for testing
          // const fakeSections = [
          //   { section: 205, queueTime: 0, sensorActive: true },
          //   { section: 220, queueTime: 0, sensorActive: true }
          // ];
          // const withFakes = [...data, ...fakeSections];
          // const randomizedData = withFakes.map(item => ({
          //   ...item,
          //   queueTime: Math.floor(Math.random() * 25)
          // }));
          // setCurrentWaitTimeData(randomizedData);
        }
      },
      token // Pass token as the auth token parameter
    );
  };

  // Fetch concession static data once on mount
  useEffect(() => {
    setLoading(true);
    
    if (!token) {
      setLoading(false);
      return;
    }

    getQueuesAPI(
      "get-concession-location-static-data-screen",
      {}, // No body data needed since token is in headers
      (data, error) => {
        if (error) {
          console.error('Failed to fetch concession static data:', error);
          setLoading(false);
        } else if (data) {
          console.log('Concession static data loaded:', data);
          
          // Set the data from API response
          setConcessionLocationStaticData(data.concessionLocationStaticData);
          setCurrentSection(data.currentSection);
          
          // Load theme based on the static data
          const league = data.concessionLocationStaticData.league
          setLeague(league)
          const venue = data.concessionLocationStaticData.venue
          setVenue(venue)
          const loadedTheme = getTheme(league, venue);
          setTheme(loadedTheme);
          
          // Extract section IDs into simple array
          const sectionIds = data.concessionLocationStaticData.sections.map(section => section.sectionId);
          setConcessionLocationSections(sectionIds);
          
          // Find the current section's nearby data
          const currentSectionData = data.concessionLocationStaticData.sections.find(
            section => section.sectionId === data.currentSection
          );
          if (currentSectionData && currentSectionData.nearby) {
            // const nearbyEntries = Object.entries(currentSectionData.nearby);
            // const withoutLast = nearbyEntries.slice(0, -3);
            // const nearbyWithoutLast = Object.fromEntries(withoutLast);
            // setNearbyCurrentSection(nearbyWithoutLast);

            // PROD 
            setNearbyCurrentSection(currentSectionData.nearby);
            // if (currentSectionData && currentSectionData.nearby) {
            //   // DEV ONLY: Add fake nearby sections for 4-card grid testing
            //   const nearbyWithFakes = {
            //     ...currentSectionData.nearby,
            //     "205": { "direction": "up", "walkTime": 4 },
            //     "220": { "direction": "left", "walkTime": 5 }
            //   };
            //   setNearbyCurrentSection(nearbyWithFakes);
            // }
          }

          // After static data is loaded, fetch current wait time data
          fetchCurrentWaitTimeData(sectionIds);
          setLoading(false);
        }
      },
      token // Pass token as the auth token parameter
    );
  }, []); // Empty dependency array - only run once

  // Auto-refresh wait time data every 30 seconds after sections are loaded
  useEffect(() => {
    if (concessionLocationSections.length > 0) {
      console.log('Starting auto-refresh for wait time data...');
      
      const interval = setInterval(() => {
        console.log('Auto-refreshing wait time data...');
        fetchCurrentWaitTimeData(concessionLocationSections);
      }, 60000); // 30 seconds

      // Cleanup function
      return () => {
        console.log('Cleaning up auto-refresh interval...');
        clearInterval(interval);
      };
    }
  }, [concessionLocationSections]); // Depends on sections being loaded

  return children({
    loading,
    concessionLocationStaticData,
    currentSection,
    league,
    venue,
    token,
    concessionLocationSections,
    nearbyCurrentSection,
    currentWaitTimeData,
    theme,
    setLoading,
    fetchCurrentWaitTimeData
  });
};

export default ConcessionScreenController;